import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Search, X } from 'lucide-react';
import { apiClient } from '@/core/api/client';
import { ENDPOINTS } from '@/core/api/endpoints';
import { ROUTES } from '@/core/config/routes';
import { ReservaSchema } from '@/data/dto/reserva';
import { AppError } from '@/core/errors/AppError';
import { Button } from '@/shared/components/Button';
import { Input } from '@/shared/components/Input';
import { formatearMonto } from '@/core/utils/formatters';
import { cn } from '@/core/utils/cn';
/**
 * Busca una reserva por código (match exacto) y muestra un mini-card con
 * resumen + botón lápiz para cargarla en el form actual navegando a su
 * detalle. Va en el header de ReservaFormPage, al lado de Guardar.
 */
export function BuscadorReservaHeader() {
    const navigate = useNavigate();
    const [q, setQ] = useState('');
    const [cargando, setCargando] = useState(false);
    const [encontrada, setEncontrada] = useState(null);
    const [error, setError] = useState(null);
    const limpiar = () => {
        setQ('');
        setEncontrada(null);
        setError(null);
    };
    const buscar = async (e) => {
        e?.preventDefault();
        const texto = q.trim();
        if (!texto)
            return;
        setCargando(true);
        setError(null);
        setEncontrada(null);
        try {
            const res = await apiClient.get(ENDPOINTS.reservas, {
                params: { pagina: 1, porPagina: 5, q: texto },
            });
            const items = (res.data?.items ?? []);
            const parsed = items
                .map((it) => {
                const safe = ReservaSchema.safeParse(it);
                return safe.success ? safe.data : null;
            })
                .filter((x) => x !== null);
            if (parsed.length === 0) {
                setError('No se encontró ninguna reserva.');
            }
            else {
                // Si hay una sola la mostramos como match directo; si hay varias el
                // usuario puede iterar abriendo cualquiera (mostramos la primera y
                // dejamos un contador "hay N coincidencias" si fuesen mas).
                setEncontrada(parsed[0]);
                if (parsed.length > 1) {
                    setError(`${parsed.length} coincidencias. Refiná la búsqueda si no es la que querés.`);
                }
            }
        }
        catch (err) {
            setError(err instanceof AppError ? err.mensaje : 'Error al buscar.');
        }
        finally {
            setCargando(false);
        }
    };
    const editar = () => {
        if (!encontrada)
            return;
        navigate(ROUTES.reservaDetalle(encontrada.id));
        limpiar();
    };
    return (_jsxs("div", { className: "relative", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsxs("div", { className: "relative", children: [_jsx(Search, { className: "pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-tertiary" }), _jsx(Input, { value: q, onChange: (e) => {
                                    setQ(e.target.value);
                                    if (encontrada || error) {
                                        setEncontrada(null);
                                        setError(null);
                                    }
                                }, onKeyDown: (e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        buscar();
                                    }
                                }, placeholder: "C\u00F3digo, nombre, email o tel\u00E9fono", className: "h-9 w-72 pl-8 text-xs" }), q ? (_jsx("button", { type: "button", onClick: limpiar, "aria-label": "Limpiar", className: "absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-text-tertiary hover:text-text-primary", children: _jsx(X, { className: "h-3.5 w-3.5" }) })) : null] }), _jsx(Button, { type: "button", variant: "secondary", size: "sm", cargando: cargando, onClick: () => buscar(), children: "Buscar" })] }), encontrada || error ? (_jsx("div", { className: cn('absolute right-0 top-full z-popover mt-1 w-80 rounded-md border bg-surface-3 p-3 shadow-modal dark:shadow-modal-dark', error ? 'border-danger' : 'border-border-subtle'), children: error ? (_jsx("p", { className: "text-xs text-danger", children: error })) : encontrada ? (_jsxs("div", { className: "flex items-start gap-3", children: [_jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("div", { className: "flex items-center gap-1.5", children: _jsx("span", { className: "font-mono text-xs font-semibold text-text-primary", children: encontrada.codigo }) }), _jsxs("div", { className: "mt-1 text-[11px] text-text-tertiary", children: [encontrada.estado, " \u00B7 ", formatearMonto(encontrada.totalNeto)] })] }), _jsx(Button, { type: "button", variant: "ghost", size: "icon", onClick: editar, "aria-label": "Editar reserva", title: "Cargar en el form", children: _jsx(Pencil, { className: "h-3.5 w-3.5" }) })] })) : null })) : null] }));
}
